import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { Edit, Save } from '@mui/icons-material'
import { Sidebar } from '../components/Sidebar'

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface User {
  id: string
  username: string
  role: string
}

interface TV {
  id: string
  name: string
  ip: string
  customName?: string
  price?: number
  status: 'on' | 'off' | 'unknown'
}

interface DevicesPageProps {
  user: User | null
  onLogout: () => void
  onNavigate: (page: string) => void
}

export const DevicesPage: React.FC<DevicesPageProps> = ({ user, onLogout, onNavigate }) => {
  const [tvs, setTvs] = useState<TV[]>([])
  const [loading, setLoading] = useState(true)
  const [editingTV, setEditingTV] = useState<TV | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editForm, setEditForm] = useState({ customName: '', price: 0 })

  useEffect(() => {
    fetchTVs()
  }, [])

  const fetchTVs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sheets/tvs`)
      if (response.ok) {
        const data = await response.json() as { tvs: TV[] }
        setTvs(data.tvs || [])
      }
    } catch (error) {
      console.error('Error fetching TVs:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (tv: TV) => {
    setEditingTV(tv)
    setEditForm({
      customName: tv.customName || '',
      price: tv.price || 0,
    })
    setEditDialogOpen(true)
  }

  const handleSave = async () => {
    if (!editingTV) return

    try {
      const response = await fetch(`${API_BASE_URL}/api/sheets/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: editingTV.id,
          customName: editForm.customName,
          price: editForm.price,
        }),
      })

      if (response.ok) {
        await fetchTVs()
        setEditDialogOpen(false)
        setEditingTV(null)
      }
    } catch (error) {
      console.error('Error updating TV:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on':
        return 'success.main'
      case 'off':
        return 'error.main'
      default:
        return 'grey.500'
    }
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f7' }}>
      {/* Sidebar */}
      <Sidebar 
        user={user}
        currentPage="devices"
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
      {/* Main Content */}
      <Box sx={{ flex: 1, p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Device Management
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Manage your gaming devices
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {tvs.map((tv) => (
            <Grid item xs={12} sm={6} md={4} key={tv.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {tv.customName || tv.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    IP: {tv.ip}
                  </Typography>
                  <Typography variant="body2" color={getStatusColor(tv.status)}>
                    Status: {tv.status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Price: ${tv.price || 0}/hour
                  </Typography>
                  <Button
                    startIcon={<Edit />}
                    onClick={() => handleEdit(tv)}
                    sx={{ mt: 1 }}
                  >
                    Edit
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Device</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Custom Name"
            value={editForm.customName}
            onChange={(e) => setEditForm({ ...editForm, customName: e.target.value })}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Price per Hour"
            type="number"
            value={editForm.price}
            onChange={(e) => setEditForm({ ...editForm, price: Number(e.target.value) })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} startIcon={<Save />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
} 